import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Moon, Star, Clock, Heart, CheckCircle2, XCircle, BookOpen, ShieldCheck, Baby, Sparkles, ChevronDown, ChevronUp, CloudMoon } from 'lucide-react';
import { RecentPurchaseToast } from './components/RecentPurchaseToast';
// Reusable FadeIn Component for gentle scroll animations
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
// Reusable CTA Button
const CTAButton = ({
  className = '',
  href
}: {
  className?: string;
  href?: string;
}) => <div className={`flex justify-center ${className}`} data-id="element-1">
    <motion.button whileHover={{
    scale: 1.04,
    boxShadow: '0 0 25px rgba(212, 175, 55, 0.5), 0 0 50px rgba(212, 175, 55, 0.25)'
  }} whileTap={{
    scale: 0.97
  }} className="cta-glow relative bg-gradient-to-r from-gold via-yellow-400 to-gold cta-shimmer text-midnight font-bold py-5 px-10 md:px-14 rounded-full transition-all duration-300 flex flex-col items-center justify-center gap-1.5 border-2 border-gold-light/40" onClick={() => href ? window.open(href, '_blank') : document.getElementById('checkout')?.scrollIntoView({
    behavior: 'smooth'
  })} data-id="element-2">
      <span className="text-lg md:text-xl tracking-wide font-extrabold drop-shadow-sm" data-id="element-3">
        SÍ, QUIERO QUE MI BEBÉ DUERMA HOY
      </span>
      <span className="text-xs md:text-sm font-semibold opacity-90 flex items-center gap-1.5" data-id="element-4">
        <ShieldCheck size={14} data-id="element-5" /> Pago seguro · Descarga inmediata
      </span>
    </motion.button>
  </div>;
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openLegal, setOpenLegal] = useState<string | null>(null);
  const faqs = [{
    q: '¿Tengo que dejarlo llorar solo?',
    a: 'No. El método incluye tu presencia física para calmarlo mientras aprende. Nunca abogamos por el abandono emocional.'
  }, {
    q: '¿Funciona si toma pecho o biberón?',
    a: 'Sí, el protocolo se adapta perfectamente a cualquier forma de alimentación. No interferimos con la nutrición de tu bebé.'
  }, {
    q: '¿Es seguro?',
    a: 'Totalmente. Se basa en el respeto a los ritmos naturales del desarrollo infantil y la biología del sueño.'
  }, {
    q: '¿Por qué cuesta solo $6.36?',
    a: 'Porque sé lo que es estar desesperada a las 3 AM y quiero que el precio no sea un obstáculo para que recuperes tu vida hoy.'
  }];
  return <div className="font-sans text-[#333333] bg-cream min-h-screen selection:bg-gold selection:text-midnight" data-id="element-6">
      <RecentPurchaseToast data-id="element-7" />
      {/* HERO SECTION */}
      <section className="relative pt-10 pb-16 px-6 overflow-hidden bg-midnight" data-id="element-8">
        {/* Subtle ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-gold rounded-full filter blur-[180px] opacity-[0.04]" data-id="element-9"></div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-10">
          <FadeIn data-id="element-11">
            <motion.p initial={{
            letterSpacing: '0.3em',
            opacity: 0
          }} whileInView={{
            letterSpacing: '0.15em',
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="text-gold uppercase text-xs md:text-sm font-semibold tracking-[0.15em] mb-4" data-id="element-12">
              ✦ Protocolo Noche de Paz ✦
            </motion.p>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-13">
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white" data-id="element-14">
              Recupera tus noches y la sonrisa de tu bebé
              <br className="hidden md:block" data-id="element-15" /> en solo{' '}
              <span className="text-gold italic whitespace-nowrap" data-id="element-16">
                72 horas
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-17">
            <div className="max-w-2xl mx-auto mb-8" data-id="element-18">
              <img src="https://cdn.magicpatterns.com/uploads/3auo8EizvFWzv5oyNT7Jh7/BANER_PV_PROTOCOLO_NOCHE_DE_PAZ_EL_METODO_DE_3_DIAS.png" alt="Protocolo Noche de Paz — eBook y guía digital mostrados en tablet y teléfono" className="w-full h-auto rounded-2xl shadow-2xl shadow-black/50 border border-white/10" data-id="element-19" />
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-20">
            <div className="max-w-2xl mx-auto mb-8" data-id="element-21">
              <p className="font-serif text-xl md:text-2xl text-gold-soft leading-relaxed mb-3" data-id="element-22">
                El método biológico para que tu bebé duerma de corrido
                <br data-id="element-23" />
                sin llantos traumáticos ni métodos crueles.
              </p>
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-gold/30 rounded-full px-6 py-3 shadow-lg" data-id="element-24">
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider" data-id="element-25">
                  Solo
                </span>
                <span className="font-serif text-3xl md:text-4xl font-bold text-white" data-id="element-26">
                  $6.36
                </span>
                <span className="text-white/80 text-sm font-medium" data-id="element-27">USD</span>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-28">
            <CTAButton data-id="element-29" />
          </FadeIn>
        </div>
      </section>

      {/* OPENING HOOK (EMOTIONAL) */}
      <section className="py-14 px-6 bg-white" data-id="element-30">
        <div className="max-w-2xl mx-auto" data-id="element-31">
          <FadeIn data-id="element-32">
            <div className="flex justify-center mb-5" data-id="element-33">
              <Clock className="text-gold" size={36} strokeWidth={1.5} data-id="element-34" />
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-center mb-4 italic text-midnight" data-id="element-35">
              "Son las 3:15 AM. Tienes al bebé en brazos, la espalda te arde y
              las lágrimas corren por tu cara."
            </p>
            <p className="text-lg text-center text-[#333333] leading-relaxed" data-id="element-36">
              Te sientes una mala madre porque "no puedes" hacer que tu hijo
              descanse. <br className="hidden md:block" data-id="element-37" />
              <strong className="text-midnight font-semibold" data-id="element-38">
                No es tu culpa. No es falta de amor. Es biología.
              </strong>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-14 px-6 bg-cream" data-id="element-39">
        <div className="max-w-3xl mx-auto" data-id="element-40">
          <FadeIn data-id="element-41">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-8 text-midnight" data-id="element-42">
              La trampa del agotamiento
            </h3>
          </FadeIn>

          <div className="bg-white p-6 md:p-8 rounded-3xl shadow-soft" data-id="element-43">
            <FadeIn delay={0.1} data-id="element-44">
              <p className="text-lg mb-5 leading-relaxed text-[#333333]" data-id="element-45">
                El agotamiento crónico no solo te quita el sueño; te quita la
                paciencia, afecta tu relación de pareja y te roba la alegría de
                tu maternidad.
              </p>
              <p className="text-lg mb-5 leading-relaxed text-[#333333]" data-id="element-46">
                Has intentado "dejarlo llorar" y terminaste llorando tú también.
                Has probado horarios rígidos que tu bebé ignora. El problema es
                que estás peleando contra el{' '}
                <strong className="text-midnight" data-id="element-47">Cortisol</strong> (la hormona
                del estrés) en lugar de aliarte con la{' '}
                <strong className="text-midnight" data-id="element-48">Melatonina</strong>.
              </p>
              <div className="bg-gold-light/50 p-5 rounded-2xl border border-gold/20 mt-6" data-id="element-49">
                <p className="text-lg leading-relaxed text-midnight/90" data-id="element-50">
                  Cuando un bebé se "pasa de rosca", su cerebro libera una
                  descarga de energía que le impide dormir, aunque esté
                  exhausto. Es una trampa biológica que te mantiene despierta
                  toda la noche.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-14 px-6 bg-white" data-id="element-51">
        <div className="max-w-3xl mx-auto" data-id="element-52">
          <FadeIn data-id="element-53">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-8 text-midnight" data-id="element-54">
              Por qué lo que has intentado no funciona
            </h3>
          </FadeIn>

          <div className="space-y-4" data-id="element-55">
            {["Los métodos de 'llanto controlado' rompen el vínculo emocional y generan ansiedad en el bebé.", "Quitarle las siestas para que 'llegue cansado' solo eleva el cortisol, haciendo que la noche sea una pesadilla de despertares.", "Mecerlo o alimentarlo hasta que se duerma crea una 'muleta' que lo obliga a despertarse cada vez que cambia de ciclo de sueño.", "Esperar a que 'crezca y se le pase' puede significar meses o años de privación de sueño para toda la familia."].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-56">
                <div className="flex gap-4 items-start p-5 rounded-2xl bg-cream border border-gold/20 hover:border-gold/50 shadow-[0_0_12px_rgba(212,175,55,0.08)] hover:shadow-[0_0_20px_rgba(212,175,55,0.18)] transition-all duration-300" data-id="element-57">
                  <XCircle className="text-red-500 shrink-0 mt-1" size={24} data-id="element-58" />
                  <p className="text-lg leading-relaxed" data-id="element-59">{item}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-16 px-6 bg-midnight text-cream relative overflow-hidden" data-id="element-60">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold rounded-full mix-blend-multiply filter blur-3xl opacity-10" data-id="element-61"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-5" data-id="element-62"></div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-63">
          <FadeIn data-id="element-64">
            <Sparkles className="text-gold mx-auto mb-4" size={36} data-id="element-65" />
            <h3 className="font-serif text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-66">
              Sincronización de Ventanas Biológicas
            </h3>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-6" data-id="element-67">
              A diferencia de los entrenadores de sueño tradicionales, este
              protocolo no se basa en el reloj de la pared, sino en los{' '}
              <strong className="text-gold-soft" data-id="element-68">picos hormonales</strong> de tu
              bebé.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-white/90" data-id="element-69">
              Identificamos el momento exacto en que su cuerpo está listo para
              el sueño profundo y lo acostamos justo ahí.{' '}
              <span className="text-gold font-medium italic" data-id="element-70">
                Sin peleas, sin gritos, solo biología aplicada.
              </span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-14 px-6 bg-cream" data-id="element-71">
        <div className="max-w-4xl mx-auto" data-id="element-72">
          <FadeIn data-id="element-73">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-10 text-midnight" data-id="element-74">
              Cómo funciona en 3 simples pasos
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-75">
            {[{
            step: '1',
            title: 'Detecta',
            desc: 'Identifica la ventana de sueño biológica de tu bebé con nuestras tablas exactas por edad.'
          }, {
            step: '2',
            title: 'Sincroniza',
            desc: 'Aplica la rutina de 15 minutos en el momento exacto del pico natural de melatonina.'
          }, {
            step: '3',
            title: 'Descansa',
            desc: 'Observa cómo tu bebé concilia el sueño de forma autónoma y duerme periodos largos.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.2} className="relative" data-id="element-76">
                <div className="bg-white p-6 rounded-3xl h-full border border-gold/25 shadow-[0_0_18px_rgba(212,175,55,0.15)] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:border-gold/50 transition-all duration-300 text-center" data-id="element-77">
                  <div className="w-14 h-14 bg-midnight text-gold rounded-full flex items-center justify-center text-xl font-serif font-bold mx-auto mb-4 shadow-[0_0_12px_rgba(212,175,55,0.25)]" data-id="element-78">
                    {item.step}
                  </div>
                  <h4 className="font-serif text-2xl font-bold mb-3 text-midnight" data-id="element-79">
                    {item.title}
                  </h4>
                  <p className="text-[#333333] leading-relaxed" data-id="element-80">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-16 px-6 bg-midnight relative overflow-hidden" data-id="element-81">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-5" data-id="element-82"></div>
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-83">
          <FadeIn data-id="element-84">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-8 text-white" data-id="element-85">
              Todo lo que recibes hoy
            </h3>
          </FadeIn>

          <ul className="space-y-4" data-id="element-86">
            {[{
            icon: <BookOpen size={24} data-id="element-87" />,
            text: 'La Guía Maestra: El Método de 3 Días paso a paso.',
            label: 'GUÍA PRINCIPAL'
          }, {
            icon: <Clock size={24} data-id="element-88" />,
            text: "Tabla de 'Ventanas de Despierto' por edad (4 a 24 meses).",
            label: 'TABLA DE REFERENCIA'
          }, {
            icon: <Moon size={24} data-id="element-89" />,
            text: 'Protocolo de Crisis: Qué hacer ante despertares nocturnos a las 3 AM.',
            label: 'PROTOCOLO DE CRISIS'
          }, {
            icon: <Star size={24} data-id="element-90" />,
            text: "Checklist del 'Santuario de Sueño': Configuración visual del cuarto.",
            label: 'CHECKLIST'
          }, {
            icon: <Baby size={24} data-id="element-91" />,
            text: 'Guía de Siestas: Cómo evitar que las siestas arruinen la noche.',
            label: 'GUÍA EXTRA'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.12} data-id="element-92">
                <li className="group relative bg-gradient-to-r from-white/[0.08] to-white/[0.03] backdrop-blur-sm border border-gold/20 hover:border-gold/50 p-5 rounded-2xl shadow-[0_0_15px_rgba(212,175,55,0.08)] hover:shadow-[0_0_25px_rgba(212,175,55,0.2)] transition-all duration-300 flex items-center gap-5" data-id="element-93">
                  {/* Numbered icon */}
                  <div className="relative shrink-0" data-id="element-94">
                    <div className="w-14 h-14 bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 text-gold rounded-2xl flex items-center justify-center shadow-[0_0_10px_rgba(212,175,55,0.15)] group-hover:shadow-[0_0_18px_rgba(212,175,55,0.3)] transition-all duration-300" data-id="element-95">
                      {item.icon}
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gold text-midnight rounded-full flex items-center justify-center text-[10px] font-bold shadow-md" data-id="element-96">
                      {i + 1}
                    </div>
                  </div>

                  {/* Text content */}
                  <div className="min-w-0" data-id="element-97">
                    <span className="text-gold/60 text-[10px] font-bold uppercase tracking-widest" data-id="element-98">
                      {item.label}
                    </span>
                    <p className="text-white/90 text-base md:text-lg font-medium leading-snug mt-0.5" data-id="element-99">
                      {item.text}
                    </p>
                  </div>

                  {/* Check indicator */}
                  <div className="shrink-0 ml-auto" data-id="element-100">
                    <div className="w-7 h-7 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors" data-id="element-101">
                      <CheckCircle2 size={16} className="text-gold" data-id="element-102" />
                    </div>
                  </div>
                </li>
              </FadeIn>)}
          </ul>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-14 px-6 bg-cream" data-id="element-103">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-104">
          <FadeIn data-id="element-105">
            <div className="bg-white p-6 rounded-3xl shadow-soft h-full border-t-4 border-gold" data-id="element-106">
              <div className="flex items-center gap-3 mb-6" data-id="element-107">
                <Heart className="text-gold" size={28} data-id="element-108" />
                <h4 className="font-serif text-2xl font-bold text-midnight" data-id="element-109">
                  Este protocolo ES para ti si:
                </h4>
              </div>
              <ul className="space-y-3" data-id="element-110">
                {['Mamás de bebés entre 4 y 24 meses que no duermen más de 3 horas seguidas.', 'Padres agotados que necesitan una solución práctica y económica hoy mismo.', 'Familias que se niegan a usar métodos agresivos de abandono emocional.', 'Mujeres que sienten que el cansancio está afectando su salud mental.'].map((text, i) => <li key={i} className="flex gap-3 items-start" data-id="element-111">
                    <CheckCircle2 className="text-gold shrink-0 mt-1" size={20} data-id="element-112" />
                    <span className="leading-relaxed" data-id="element-113">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-114">
            <div className="bg-midnight/5 p-6 rounded-3xl shadow-soft h-full border-t-4 border-midnight/30 relative overflow-hidden" data-id="element-115">
              <div className="absolute top-0 right-0 w-24 h-24 bg-midnight/5 rounded-full filter blur-2xl" data-id="element-116"></div>
              <div className="relative z-10" data-id="element-117">
                <div className="flex items-center gap-3 mb-6" data-id="element-118">
                  <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center" data-id="element-119">
                    <XCircle className="text-red-400" size={22} data-id="element-120" />
                  </div>
                  <h4 className="font-serif text-2xl font-bold text-midnight" data-id="element-121">
                    NO es para ti si:
                  </h4>
                </div>
                <ul className="space-y-3" data-id="element-122">
                  {["Personas que buscan una 'pastilla mágica' sin cambiar la rutina del hogar.", 'Bebés menores de 4 meses (su ritmo circadiano aún no está maduro).', 'Padres que no están dispuestos a seguir el protocolo durante al menos 3 días constantes.'].map((text, i) => <li key={i} className="flex gap-3 items-start" data-id="element-123">
                      <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center shrink-0 mt-1" data-id="element-124">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full" data-id="element-125"></div>
                      </div>
                      <span className="leading-relaxed text-[#333333]" data-id="element-126">
                        {text}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF / TIMELINE */}
      <section className="py-14 px-6 bg-white" data-id="element-127">
        <div className="max-w-3xl mx-auto text-center" data-id="element-128">
          <FadeIn data-id="element-129">
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-midnight" data-id="element-130">
              Qué cambia en 72 horas
            </h3>
          </FadeIn>

          <div className="relative" data-id="element-131">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gold/20 -translate-x-1/2" data-id="element-132"></div>

            <div className="space-y-8 relative z-10" data-id="element-133">
              {[{
              day: 'Día 1',
              title: 'La primera noche',
              desc: 'Será de ajuste. Comenzarás a aplicar las ventanas biológicas y notarás los primeros bostezos a la hora correcta.'
            }, {
              day: 'Día 2',
              title: 'La segunda noche',
              desc: 'Verás cómo los despertares nocturnos se reducen a la mitad. El bebé comienza a conectar ciclos.'
            }, {
              day: 'Día 3',
              title: 'La tercera noche',
              desc: 'La mayoría de los bebés logran dormir su primer bloque de 6 a 8 horas seguidas, permitiéndote despertar con energía por primera vez en meses.'
            }].map((item, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-134">
                  <div className={`flex flex-col md:flex-row items-center gap-4 md:gap-10 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-135">
                    <div className={`flex-1 w-full md:text-${i % 2 !== 0 ? 'left' : 'right'}`} data-id="element-136">
                      <div className="inline-block bg-midnight text-gold px-4 py-1 rounded-full text-sm font-bold mb-2 shadow-sm" data-id="element-137">
                        {item.day}
                      </div>
                      <h4 className="font-serif text-2xl font-bold mb-1" data-id="element-138">
                        {item.title}
                      </h4>
                      <p className="text-[#333333] leading-relaxed" data-id="element-139">
                        {item.desc}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-white border-4 border-gold rounded-full flex items-center justify-center shrink-0 shadow-soft z-10" data-id="element-140">
                      <Moon className="text-gold" size={16} data-id="element-141" />
                    </div>
                    <div className="flex-1 hidden md:block" data-id="element-142"></div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* EXPECTED RESULTS + SOCIAL PROOF */}
      <section className="py-16 px-6 bg-midnight relative overflow-hidden" data-id="element-143">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-5" data-id="element-144"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-gold rounded-full filter blur-[150px] opacity-[0.06]" data-id="element-145"></div>

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-146">
          <FadeIn data-id="element-147">
            <p className="text-gold uppercase text-xs font-bold tracking-widest text-center mb-3" data-id="element-148">
              ✦ Resultados Esperados ✦
            </p>
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-4 text-white" data-id="element-149">
              Lo que las mamás pueden esperar
            </h3>
            <p className="text-white/60 text-center text-base mb-10 max-w-xl mx-auto" data-id="element-150">
              Basado en los principios de sincronización biológica del protocolo
            </p>
          </FadeIn>

          <div className="space-y-4" data-id="element-151">
            {[{
            time: 'Noche 1',
            emoji: '🌙',
            title: 'Los primeros bostezos a la hora correcta',
            desc: 'Identificas la ventana biológica de tu bebé y notas cómo su cuerpo responde al nuevo horario.'
          }, {
            time: 'Noche 2',
            emoji: '😴',
            title: 'Los despertares se reducen a la mitad',
            desc: 'Tu bebé comienza a conectar ciclos de sueño. Tú empiezas a dormir bloques más largos.'
          }, {
            time: 'Noche 3',
            emoji: '✨',
            title: 'Primer bloque de 6-8 horas seguidas',
            desc: 'Tu bebé duerme de corrido. Tú despiertas descansada por primera vez en meses.'
          }, {
            time: 'Semana 1',
            emoji: '💪',
            title: 'La rutina se consolida',
            desc: 'Las siestas se regulan, las noches son predecibles. Recuperas la paciencia y la energía.'
          }, {
            time: 'Semana 2',
            emoji: '☀️',
            title: 'Una mamá renovada',
            desc: 'Sin ojeras, con energía para disfrutar tu maternidad. Tu pareja también nota la diferencia.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-152">
                <div className="group flex items-start gap-4 bg-white/[0.06] border border-gold/15 hover:border-gold/40 rounded-2xl p-5 shadow-[0_0_12px_rgba(212,175,55,0.05)] hover:shadow-[0_0_22px_rgba(212,175,55,0.15)] transition-all duration-300" data-id="element-153">
                  <div className="shrink-0 w-14 h-14 bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/25 rounded-2xl flex items-center justify-center text-2xl group-hover:shadow-[0_0_12px_rgba(212,175,55,0.25)] transition-all duration-300" data-id="element-154">
                    {item.emoji}
                  </div>
                  <div className="min-w-0" data-id="element-155">
                    <div className="flex items-center gap-2 mb-1" data-id="element-156">
                      <span className="text-gold text-xs font-bold uppercase tracking-wider" data-id="element-157">
                        {item.time}
                      </span>
                    </div>
                    <h4 className="text-white font-bold text-base md:text-lg leading-snug" data-id="element-158">
                      {item.title}
                    </h4>
                    <p className="text-white/60 text-sm leading-relaxed mt-1" data-id="element-159">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          {/* Social proof counter */}
          <FadeIn delay={0.5} data-id="element-160">
            <div className="mt-10 text-center" data-id="element-161">
              <div className="inline-flex items-center gap-3 bg-gold/10 border border-gold/25 rounded-full px-6 py-3" data-id="element-162">
                <div className="flex -space-x-2" data-id="element-163">
                  {['👩', '👩‍🦱', '👩‍🦰', '🧑‍🦱'].map((emoji, i) => <div key={i} className="w-8 h-8 bg-midnight border-2 border-gold/30 rounded-full flex items-center justify-center text-sm" data-id="element-164">
                      {emoji}
                    </div>)}
                </div>
                <p className="text-white/90 text-sm font-medium" data-id="element-165">
                  <strong className="text-gold" data-id="element-166">+200 mamás</strong> ya confían
                  en el Protocolo
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 px-6 bg-cream" data-id="element-167">
        <div className="max-w-3xl mx-auto" data-id="element-168">
          <FadeIn data-id="element-169">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-center mb-8 text-midnight" data-id="element-170">
              Preguntas Frecuentes
            </h3>
          </FadeIn>

          <div className="space-y-3" data-id="element-171">
            {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-172">
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 hover:border-gold/30 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-173">
                  <div className="p-5 flex justify-between items-center gap-4" data-id="element-174">
                    <h4 className="font-medium text-lg" data-id="element-175">{faq.q}</h4>
                    {openFaq === i ? <ChevronUp className="text-gold shrink-0" data-id="element-176" /> : <ChevronDown className="text-gold shrink-0" data-id="element-177" />}
                  </div>
                  {openFaq === i && <div className="px-5 pb-5 text-[#333333] leading-relaxed border-t border-gray-50 pt-3" data-id="element-178">
                      {faq.a}
                    </div>}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE SECTION */}
      <section className="py-14 px-6 bg-white" data-id="element-179">
        <div className="max-w-xl mx-auto" data-id="element-180">
          <FadeIn data-id="element-181">
            <div className="relative" data-id="element-182">
              {/* Shield icon overlapping the card top */}
              <div className="flex justify-center mb-[-32px] relative z-20" data-id="element-183">
                <div className="w-16 h-16 bg-gradient-to-br from-gold to-gold-hover rounded-full flex items-center justify-center shadow-lg shadow-gold/30" data-id="element-184">
                  <ShieldCheck className="text-midnight" size={32} strokeWidth={2.5} data-id="element-185" />
                </div>
              </div>

              {/* Card */}
              <div className="bg-midnight border-2 border-gold/30 rounded-3xl pt-14 pb-10 px-8 md:px-12 text-center shadow-2xl shadow-midnight/30 relative overflow-hidden" data-id="element-186">
                {/* Subtle background glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 bg-gold rounded-full filter blur-[100px] opacity-10" data-id="element-187"></div>

                <div className="relative z-10" data-id="element-188">
                  <h3 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-189">
                    Garantía Incondicional
                    <br data-id="element-190" />
                    <span className="text-gold" data-id="element-191">de 7 Días</span>
                  </h3>

                  <p className="font-serif text-lg md:text-xl text-gold-soft leading-relaxed mb-4 italic" data-id="element-192">
                    Si en los próximos 7 días sientes que este sistema no
                    entrega lo prometido, te devolvemos el 100% de tu dinero.
                  </p>

                  <p className="text-white/60 text-base mb-6" data-id="element-193">
                    Sin preguntas. Sin condiciones. Sin burocracia.
                  </p>

                  <div className="w-full h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent mb-6" data-id="element-194"></div>

                  <p className="text-white font-bold text-lg md:text-xl uppercase tracking-wider leading-relaxed" data-id="element-195">
                    Todo el riesgo es nuestro.
                    <br data-id="element-196" />
                    Todo el beneficio es tuyo.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section id="checkout" className="py-16 px-6 bg-midnight text-white relative overflow-hidden" data-id="element-197">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10" data-id="element-198"></div>

        <div className="max-w-2xl mx-auto text-center relative z-10" data-id="element-199">
          <FadeIn data-id="element-200">
            <h3 className="font-serif text-3xl md:text-5xl font-bold mb-6" data-id="element-201">
              Recupera tu sueño y la paz en tu hogar hoy mismo.
            </h3>
            <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed" data-id="element-202">
              Por menos de lo que cuesta un café y un muffin, tendrás la hoja de
              ruta exacta para que tu bebé duerma.
            </p>

            <div className="bg-gradient-to-b from-gold/10 to-white/5 backdrop-blur-md border border-gold/25 shadow-[0_0_30px_rgba(212,175,55,0.1)] rounded-3xl p-8 md:p-12 mb-10" data-id="element-203">
              <p className="text-sm uppercase tracking-widest text-gold mb-4 font-semibold" data-id="element-204">
                Pago Único
              </p>

              {/* Discount badge */}
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/30 rounded-full px-4 py-1.5 mb-4" data-id="element-205">
                <span className="text-red-400 font-bold text-sm" data-id="element-206">
                  -76% DESCUENTO
                </span>
              </div>

              {/* Original price struck through */}
              <div className="flex justify-center items-center gap-3 mb-2" data-id="element-207">
                <span className="text-red-400 line-through text-2xl font-medium opacity-80" data-id="element-208">
                  $27.00 USD
                </span>
              </div>

              {/* Sale price */}
              <div className="flex justify-center items-start gap-1 mb-2 text-white" data-id="element-209">
                <span className="text-3xl font-bold mt-2" data-id="element-210">$</span>
                <span className="text-7xl font-serif font-bold" data-id="element-211">6.36</span>
                <span className="text-xl mt-8" data-id="element-212">USD</span>
              </div>

              <p className="text-gold-soft text-sm font-medium mb-6" data-id="element-213">
                Ahorras $20.64 hoy
              </p>

              <p className="text-white/80 mb-8" data-id="element-214">
                Acceso instantáneo en PDF a tu correo.
              </p>

              <CTAButton className="w-full text-xl py-5" href="https://vitalyforms.com/cart/48537133318364:1" data-id="element-215" />
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-white/60" data-id="element-216">
              <ShieldCheck size={16} data-id="element-217" />
              <span data-id="element-218">Pago 100% seguro. Sin suscripciones ocultas.</span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-midnight text-center px-6 py-12 md:py-16" data-id="element-219">
        <div className="max-w-3xl mx-auto space-y-8" data-id="element-220">
          {/* Legal Links */}
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs md:text-sm" data-id="element-221">
            {[{
            id: 'privacidad',
            label: 'Políticas de Privacidad'
          }, {
            id: 'terminos',
            label: 'Términos y Condiciones'
          }, {
            id: 'reembolso',
            label: 'Políticas de Reembolso'
          }, {
            id: 'contacto',
            label: 'Contacto'
          }].map(item => <button key={item.id} type="button" onClick={() => setOpenLegal(openLegal === item.id ? null : item.id)} className={`transition-colors cursor-pointer ${openLegal === item.id ? 'text-gold' : 'text-white/80 hover:text-gold'}`} data-id="element-222">
                {item.label}
              </button>)}
          </nav>

          {/* Expandable Legal Content */}
          {openLegal && <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-left max-w-2xl mx-auto" data-id="element-223">
              <div className="flex justify-between items-center mb-4" data-id="element-224">
                <h5 className="text-white font-semibold text-sm" data-id="element-225">
                  {openLegal === 'privacidad' && 'Políticas de Privacidad'}
                  {openLegal === 'terminos' && 'Términos y Condiciones'}
                  {openLegal === 'reembolso' && 'Políticas de Reembolso'}
                  {openLegal === 'contacto' && 'Contacto'}
                </h5>
                <button type="button" onClick={() => setOpenLegal(null)} className="text-white/50 hover:text-white transition-colors text-xs" data-id="element-226">
                  ✕ Cerrar
                </button>
              </div>
              <div className="text-[#CCCCCC]/80 text-xs md:text-sm leading-relaxed space-y-3" data-id="element-227">
                {openLegal === 'privacidad' && <>
                    <p data-id="element-228">
                      En Protocolo Noche de Paz, respetamos tu privacidad.
                      Recopilamos únicamente la información necesaria para
                      procesar tu compra (nombre y correo electrónico).
                    </p>
                    <p data-id="element-229">
                      No compartimos, vendemos ni distribuimos tus datos
                      personales a terceros. Tu información se utiliza
                      exclusivamente para enviarte el producto adquirido y
                      comunicaciones relacionadas.
                    </p>
                    <p data-id="element-230">
                      Puedes solicitar la eliminación de tus datos en cualquier
                      momento escribiéndonos a nuestro correo de contacto.
                    </p>
                    <p className="mt-4 pt-3 border-t border-white/10" data-id="element-231">
                      <strong className="text-white" data-id="element-232">
                        Descargo de responsabilidad:
                      </strong>{' '}
                      La información proporcionada en el Protocolo "Noche de
                      Paz" tiene fines educativos e informativos únicamente. No
                      sustituye el consejo, diagnóstico o tratamiento de un
                      pediatra o profesional de la salud. Siempre consulte con
                      su médico antes de realizar cambios en la rutina de sueño
                      o alimentación de su bebé. El uso de este método es
                      responsabilidad exclusiva del usuario.
                    </p>
                  </>}
                {openLegal === 'terminos' && <>
                    <p data-id="element-233">
                      Al adquirir el Protocolo "Noche de Paz", aceptas que se
                      trata de un producto digital (PDF) de descarga inmediata
                      con fines educativos e informativos.
                    </p>
                    <p data-id="element-234">
                      El contenido no sustituye el consejo médico profesional.
                      El uso del método es responsabilidad exclusiva del
                      comprador.
                    </p>
                    <p data-id="element-235">
                      Queda prohibida la reproducción, distribución o reventa
                      del material sin autorización expresa.
                    </p>
                    <p className="mt-4 pt-3 border-t border-white/10" data-id="element-236">
                      <strong className="text-white" data-id="element-237">
                        Nota sobre plataformas:
                      </strong>{' '}
                      Este sitio no forma parte del sitio web de Facebook o de
                      Meta Platforms, Inc. Además, este sitio NO está respaldado
                      por Facebook de ninguna manera. FACEBOOK es una marca
                      comercial de META PLATFORMS, Inc.
                    </p>
                  </>}
                {openLegal === 'reembolso' && <>
                    <p data-id="element-238">
                      Ofrecemos una{' '}
                      <strong className="text-white" data-id="element-239">
                        garantía incondicional de 7 días
                      </strong>
                      . Si dentro de los primeros 7 días desde tu compra sientes
                      que el Protocolo "Noche de Paz" no cumple con lo
                      prometido, te devolvemos el 100% de tu dinero.
                    </p>
                    <p data-id="element-240">
                      Sin preguntas, sin condiciones, sin burocracia. Solo
                      escríbenos a nuestro correo de contacto y procesaremos tu
                      reembolso de inmediato.
                    </p>
                    <p data-id="element-241">Todo el riesgo es nuestro. Todo el beneficio es tuyo.</p>
                  </>}
                {openLegal === 'contacto' && <>
                    <p data-id="element-242">¿Tienes preguntas o necesitas ayuda? Escríbenos:</p>
                    <p className="text-gold" data-id="element-243">
                      📧 soporte@protoconolochedepaz.com
                    </p>
                    <p data-id="element-244">Respondemos en un plazo máximo de 24 horas hábiles.</p>
                  </>}
              </div>
            </div>}

          <div className="w-16 h-px bg-white/10 mx-auto" data-id="element-245"></div>

          {/* Copyright */}
          <p className="text-xs text-[#CCCCCC]/50" data-id="element-246">
            © 2026 Protocolo Noche de Paz. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}